import { Box, Stack } from '@mui/material';
import { useRef } from 'react';
import { Outlet, useMatch } from 'react-router-dom';

import { NotFoundError } from '@/components/display/NotFoundError/NotFoundError';
import { useRequiredParam } from '@/hooks/useRequiredParam';
import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';
import { HouseholdChat } from '@/modules/household/chat/components/HouseholdChat';
import { HouseholdChatProvider } from '@/modules/household/chat/contexts/HouseholdChat.provider';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { HouseholdDetailsAwareRoute } from '@/modules/household/contexts/HouseholdDetailsAwareRoute';
import { HouseholdValidationQuestionsBanner } from '@/modules/validationQuestions/ValidationQuestionsBanner/HouseholdValidationQuestionsBanner';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';

import { HouseholdSidebar } from '../Sidebar/HouseholdSidebar';
import { ClientDetailsAppBar } from './AppBar/ClientDetailsAppBar';
import { PrimaryAppBar } from './AppBar/PrimaryAppBar';
import { AppBarLayout } from './AppBarLayout/AppBarLayout';

interface ClientDetailsLayoutInnerProps {
  householdId: string;
}

function ClientDetailsLayoutInner({
  householdId,
}: ClientDetailsLayoutInnerProps) {
  const { householdNotFound } = useHouseholdDetailsContext();
  const enhanced = useFeatureFlag('enhanced_household_onboarding_flow');
  const isOverview = useMatch({
    path: getCompletePathFromRouteKey(ROUTE_KEYS.HOUSEHOLD_DETAILS_OVERVIEW, {
      householdId,
    }),
    end: true,
  });
  // The content area below the app bar (page column + docked chat pane). The
  // chat pane's resize hook measures this container to translate drag
  // positions into pane widths.
  const chatContainerRef = useRef<HTMLDivElement | null>(null);

  if (householdNotFound) {
    const notFoundReturnPath = getCompletePathFromRouteKey(
      ROUTE_KEYS.HOUSEHOLDS_LIST,
      {}
    );
    return (
      <AppBarLayout
        hideSidebar
        sidebarLayout="primary"
        slots={{
          AppBar: {
            component: PrimaryAppBar,
            props: {},
          },
        }}
      >
        <Stack
          sx={{
            justifyContent: 'center',
            height: '100%',
            alignItems: 'center',
          }}
        >
          <NotFoundError
            title="Unable to load client"
            message="The client you are looking for could not be found, or you may not have the necessary access"
            buttonText="Return to clients"
            returnPath={notFoundReturnPath}
            sx={{ maxWidth: '350px' }}
          />
        </Stack>
      </AppBarLayout>
    );
  }

  return (
    // The chat provider wraps the whole household layout so the app bar
    // toggle and the docked pane share state, and so the conversation
    // survives navigation between household pages. It is mounted for all
    // users (cheap until a conversation starts); the toggle button gates
    // access on tenant AI capabilities. The key remounts the provider (and
    // the layout beneath it) when switching households, guaranteeing all
    // household-scoped state — chat included — is reset rather than carried
    // across households.
    <HouseholdChatProvider key={householdId} householdId={householdId}>
      <AppBarLayout
        // This id is used to indicate to posthog where surveys should show up; right now, we're targeting for them to show up
        // on the client details page and any subroutes
        id="posthog-survey-target"
        sidebarLayout="secondary"
        slots={{
          AppBar: {
            component: ClientDetailsAppBar,
            props: {},
          },
          LeftSidebar: {
            component: HouseholdSidebar,
            props: {
              householdId,
            },
          },
        }}
      >
        <Box ref={chatContainerRef}>
          <AppBarLayout
            sidebarLayout="primary"
            hideAppBar
            slots={{
              // Replaces GlobalSidebar, which only renders content on the
              // households-list route — i.e. it was always null inside this
              // layout.
              RightSidebar: {
                component: HouseholdChat,
                props: { containerRef: chatContainerRef },
              },
            }}
          >
            {enhanced && !isOverview && (
              <HouseholdValidationQuestionsBanner householdId={householdId} />
            )}
            <Outlet />
          </AppBarLayout>
        </Box>
      </AppBarLayout>
    </HouseholdChatProvider>
  );
}

export function ClientDetailsLayout() {
  const householdId = useRequiredParam('householdId');

  return (
    <HouseholdDetailsAwareRoute householdId={householdId}>
      <ClientDetailsLayoutInner householdId={householdId} />
    </HouseholdDetailsAwareRoute>
  );
}
